import { useEffect, useState } from 'react';
import { AlertCircle, Pause, Play, Square } from 'lucide-react';

import { useRobotStore } from '@/stores/useRobotStore';
import {
  loadRolloutTask,
  pauseRolloutTask,
  resumeRolloutTask,
  startRolloutTask,
  stopRolloutTask,
  unloadRolloutTask,
} from '@/lib/task-api';
import { fetchTasks, type Task } from '@/lib/checkpoint-api';
import type { RolloutState } from '@shared/lib/api/rollout';
import { useRolloutState } from '@shared/hooks/useRolloutState';

// Effective UI state — collapses the brief "request sent but worker hasn't
// reported yet" window into the same bucket as 'loading' so the title and
// button match the user's intent rather than the raw heartbeat.
type EffectiveState =
  | 'select'    // idle + no pending load → user needs to pick + load
  | 'loading'   // either rolloutState 'loading' OR idle + pending load
  | 'ready'
  | 'starting'
  | 'running'   // rolloutState 'inferring'
  | 'paused'
  | 'stopping'
  | 'error';

function getEffectiveState(
  rolloutState: RolloutState,
  isInLoadingPhase: boolean,
): EffectiveState {
  if (isInLoadingPhase) return 'loading';
  if (rolloutState === 'idle') return 'select';
  if (rolloutState === 'inferring') return 'running';
  return rolloutState;
}

const STATE_DISPLAY: Record<EffectiveState, { label: string; color: string }> = {
  select:   { label: 'Select a Task', color: 'text-foreground' },
  loading:  { label: 'Loading…',      color: 'text-warning'    },
  ready:    { label: 'Ready',         color: 'text-warning'    },
  starting: { label: 'Starting…',     color: 'text-warning'    },
  running:  { label: 'Running',       color: 'text-success'    },
  paused:   { label: 'Paused',        color: 'text-warning'    },
  stopping: { label: 'Stopping…',     color: 'text-muted'      },
  error:    { label: 'Error',         color: 'text-danger'     },
};

// ── Local action runner ──────────────────────────────────────────────────────

function useActionRunner() {
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runAction = async (key: string, fn: () => Promise<unknown>, onSuccess?: () => void) => {
    setPendingAction(key);
    setErrorMessage(null);
    try {
      await fn();
      onSuccess?.();
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingAction(null);
    }
  };

  return {
    pendingAction,
    errorMessage,
    runAction,
    clearError: () => setErrorMessage(null),
  };
}

// ── Persistence (just the last-selected task name) ───────────────────────────

const TASK_STORAGE_KEY = 'user_task_name';

// ── Panel ────────────────────────────────────────────────────────────────────

export function CheckpointPanel() {
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const { pendingAction, errorMessage, runAction, clearError } = useActionRunner();

  const {
    serviceOnline,
    rolloutState,
    errorMessage: serviceErrorMessage,
  } = useRolloutState(heartbeats);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [loadRequested, setLoadRequested] = useState(false);
  const [taskName, setTaskName] = useState<string>(() => {
    try { return localStorage.getItem(TASK_STORAGE_KEY) ?? ''; } catch { return ''; }
  });
  const selectTask = (v: string) => {
    setTaskName(v);
    try { localStorage.setItem(TASK_STORAGE_KEY, v); } catch {}
  };

  useEffect(() => {
    let cancelled = false;
    setTasksLoading(true);
    setTasksError(null);
    fetchTasks()
      .then((list) => {
        if (cancelled) return;
        setTasks(list);
        setTasksLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setTasks([]);
        setTasksLoading(false);
        setTasksError(e instanceof Error ? e.message : String(e));
      });
    return () => { cancelled = true; };
  }, []);

  // Auto-select first task when list arrives.
  useEffect(() => {
    if (tasks.length === 0) return;
    if (!taskName || !tasks.some((task) => task.name === taskName)) {
      selectTask(tasks[0].name);
    }
  }, [tasks, taskName]);

  useEffect(() => {
    if (rolloutState !== 'idle' || !serviceOnline) {
      setLoadRequested(false);
    }
  }, [rolloutState, serviceOnline]);

  const isLoadPending = pendingAction === 'load' || loadRequested;
  const canLoad    = rolloutState === 'idle' && taskName !== '' && !loadRequested;

  const loadSelectedCheckpoint = async () => {
    const task = tasks.find((t) => t.name === taskName);
    setLoadRequested(true);
    try {
      await loadRolloutTask({
        task_name: taskName,
        ml_flow_run_name: task?.ml_flow_run_name ?? '',
      });
    } catch (error) {
      setLoadRequested(false);
      throw error;
    }
  };

  // Task switching gates
  const [switchTarget, setSwitchTarget] = useState<string | null>(null);
  const [blockerOpen, setBlockerOpen] = useState(false);
  const [pendingLoadAfterUnload, setPendingLoadAfterUnload] = useState<string | null>(null);

  // True whenever the user has effectively asked for a model load — covers the
  // raw 'loading' heartbeat AND the gaps before/after it (request sent but
  // worker not yet alive; switch in progress between unload and re-load).
  const isInLoadingPhase =
    rolloutState === 'loading' ||
    (rolloutState === 'idle' && isLoadPending) ||
    pendingLoadAfterUnload !== null;

  const RUNNING_STATES: RolloutState[] = ['starting', 'inferring', 'paused', 'stopping'];
  const SWITCHABLE_LOADED_STATES: RolloutState[] = ['ready', 'loading'];

  const handleTaskClick = (newTaskName: string) => {
    if (newTaskName === taskName) return;
    if (rolloutState === 'idle' || rolloutState === 'error') {
      selectTask(newTaskName);
      return;
    }
    if (RUNNING_STATES.includes(rolloutState)) {
      setBlockerOpen(true);
      return;
    }
    if (SWITCHABLE_LOADED_STATES.includes(rolloutState)) {
      setSwitchTarget(newTaskName);
    }
  };

  const confirmSwitch = () => {
    const newName = switchTarget;
    setSwitchTarget(null);
    if (!newName) return;
    setPendingLoadAfterUnload(newName);
    runAction('unload', () => unloadRolloutTask());
  };

  const cancelSwitch = () => setSwitchTarget(null);

  // Auto-load the new task once the unload reaches idle.
  useEffect(() => {
    if (!pendingLoadAfterUnload || rolloutState !== 'idle' || !serviceOnline) return;
    const newName = pendingLoadAfterUnload;
    setPendingLoadAfterUnload(null);
    const task = tasks.find((t) => t.name === newName);
    if (!task) return;
    selectTask(newName);
    setLoadRequested(true);
    runAction('load', () =>
      loadRolloutTask({ task_name: task.name, ml_flow_run_name: task.ml_flow_run_name }),
    );
  }, [pendingLoadAfterUnload, rolloutState, serviceOnline, tasks]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="relative flex flex-col gap-3">

      {!serviceOnline && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-surface/80 backdrop-blur-sm">
          <div className="max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-lg">
            <h3 className="mb-2 text-base font-semibold text-foreground">Rollout Service Offline</h3>
            <p className="text-sm text-muted">
              Start it on the robot with{' '}
              <code className="text-xs bg-surface-muted px-1.5 py-0.5 rounded">rolloutservice start</code>{' '}
              and wait for the heartbeat.
            </p>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-xl border border-danger/40 bg-danger/8 px-3 py-2 text-xs text-danger">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span className="break-all flex-1">{errorMessage}</span>
          <button onClick={clearError} className="text-danger/80 hover:text-danger">×</button>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {(() => {
          const info = STATE_DISPLAY[getEffectiveState(rolloutState, isInLoadingPhase)];
          return <div className={`text-3xl font-semibold ${info.color}`}>{info.label}</div>;
        })()}

        {serviceErrorMessage && (
          <div className="flex items-start gap-2 rounded-xl border border-danger/40 bg-danger/8 px-3 py-2 text-xs text-danger">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span className="break-all">{serviceErrorMessage}</span>
          </div>
        )}

        {tasksError && (
          <div className="flex items-start gap-2 rounded-xl border border-danger/40 bg-danger/8 px-3 py-2 text-xs text-danger">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span className="break-all">{tasksError}</span>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="text-xs text-muted">Select Task</div>
          {tasks.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {tasks.map((task) => {
                const isSelected = task.name === taskName;
                return (
                  <button
                    key={task.name}
                    onClick={() => handleTaskClick(task.name)}
                    className={`text-left rounded-md p-3 bg-surface-muted border-2 transition-colors hover:bg-surface-strong ${
                      isSelected ? 'border-orange-400' : 'border-transparent'
                    }`}
                  >
                    <div className="text-sm font-semibold text-orange-400 truncate">{task.name}</div>
                    <div className="text-xs text-muted mt-1">{task.description}</div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-md bg-surface-muted px-3 py-2 text-sm text-muted">
              {tasksLoading ? 'Loading tasks…' : 'No tasks available'}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {rolloutState === 'idle' && !isInLoadingPhase && (
            <button
              disabled={!canLoad || !serviceOnline || pendingAction !== null}
              onClick={() => runAction('load', loadSelectedCheckpoint)}
              className="w-full rounded-md border border-border bg-surface-muted px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-strong transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Load
            </button>
          )}

          <div className="flex gap-3">
            {rolloutState === 'ready' && !isInLoadingPhase && (
              <button
                disabled={!serviceOnline || pendingAction === 'start'}
                onClick={() => runAction('start', () => startRolloutTask())}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-success/80 px-4 py-3 text-base font-semibold text-white hover:bg-success transition disabled:opacity-50"
              >
                <Play className="h-5 w-5" />
                {pendingAction === 'start' ? 'Starting…' : 'Start'}
              </button>
            )}
            {isInLoadingPhase && (
              <button
                disabled={pendingAction === 'unload'}
                onClick={() => {
                  setLoadRequested(false);
                  setPendingLoadAfterUnload(null);
                  runAction('unload', () => unloadRolloutTask());
                }}
                className="flex-1 rounded-md bg-danger/80 px-4 py-3 text-base font-semibold text-white hover:bg-danger transition disabled:opacity-50"
              >
                {pendingAction === 'unload' ? 'Aborting…' : 'Abort Model Loading'}
              </button>
            )}
            {rolloutState === 'starting' && (
              <button
                disabled={pendingAction === 'stop'}
                onClick={() => runAction('stop', () => stopRolloutTask())}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface-muted px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-strong transition disabled:opacity-50"
              >
                <Square className="h-5 w-5" />
                {pendingAction === 'stop' ? 'Cancelling…' : 'Cancel'}
              </button>
            )}
            {rolloutState === 'inferring' && (
              <>
                <button
                  disabled={pendingAction === 'pause'}
                  onClick={() => runAction('pause', () => pauseRolloutTask())}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-warning/80 px-4 py-3 text-base font-semibold text-white hover:bg-warning transition disabled:opacity-50"
                >
                  <Pause className="h-5 w-5" />
                  {pendingAction === 'pause' ? 'Pausing…' : 'Pause'}
                </button>
                <button
                  disabled={pendingAction === 'stop'}
                  onClick={() => runAction('stop', () => stopRolloutTask())}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface-muted px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-strong transition disabled:opacity-50"
                >
                  <Square className="h-5 w-5" />
                  {pendingAction === 'stop' ? 'Stopping…' : 'Stop'}
                </button>
              </>
            )}
            {rolloutState === 'paused' && (
              <>
                <button
                  disabled={pendingAction === 'resume'}
                  onClick={() => runAction('resume', () => resumeRolloutTask())}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-success/80 px-4 py-3 text-base font-semibold text-white hover:bg-success transition disabled:opacity-50"
                >
                  <Play className="h-5 w-5" />
                  {pendingAction === 'resume' ? 'Resuming…' : 'Resume'}
                </button>
                <button
                  disabled={pendingAction === 'stop'}
                  onClick={() => runAction('stop', () => stopRolloutTask())}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface-muted px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-strong transition disabled:opacity-50"
                >
                  <Square className="h-5 w-5" />
                  {pendingAction === 'stop' ? 'Stopping…' : 'Stop'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {(switchTarget || blockerOpen) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-xl">
            {switchTarget ? (
              <>
                <p className="text-base text-foreground mb-5">
                  <span className="font-semibold text-orange-400">{taskName}</span> is currently loaded, do you want to switch?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelSwitch}
                    className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-surface-strong transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSwitch}
                    className="rounded-md bg-warning/80 hover:bg-warning px-4 py-2 text-sm font-semibold text-white transition"
                  >
                    Switch
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-base text-foreground mb-5">Stop the running policy before changing tasks.</p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setBlockerOpen(false)}
                    className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-surface-strong transition"
                  >
                    OK
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
